/**
 * RichTextEditor — a deliberately restrained editorial writing surface for
 * Maison Touré Journal articles. Only supports:
 *   • Paragraph
 *   • Heading 2 / Heading 3
 *   • Bold / Italic
 *   • Block quote
 *   • Hyperlink
 *
 * Everything else is disabled. Stores semantic HTML which the public
 * article renders through the .prose-editorial class so authors can
 * NEVER override typography, spacing or colour.
 */
import { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

const editorProseStyles = {
    minHeight: 320,
    padding: '24px 26px',
    background: 'var(--sand-2, #F5F0E1)',
    border: '1px solid var(--rule)',
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: 17,
    lineHeight: 1.75,
    color: 'var(--ink)',
    outline: 'none',
};

const btn = (active) => ({
    padding: '6px 12px',
    fontFamily: "'Jost', sans-serif",
    fontSize: 10,
    letterSpacing: '0.28em',
    textTransform: 'uppercase',
    fontWeight: active ? 500 : 300,
    color: active ? 'var(--ink)' : 'var(--ink-2)',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '1px solid var(--ink)' : '1px solid transparent',
    cursor: 'pointer',
    marginBottom: -1,
});

export default function RichTextEditor({ value, onChange, testid = 'rich-editor' }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                bulletList: false,
                orderedList: false,
                listItem: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                strike: false,
                blockquote: {},
                /* StarterKit v3 ships Link. Disable it here so we can
                   register a fully configured Link extension below without
                   the "Duplicate extension names" console warning. */
                link: false,
            }),
            Link.configure({
                openOnClick: false,
                autolink: false,
                HTMLAttributes: { rel: 'noopener noreferrer' },
            }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html === '<p></p>' ? '' : html);
        },
    });

    /* Sync when parent replaces the value (e.g. loading a different journal
       entry into the same editor instance). */
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = value || '';
        if (incoming && incoming !== current) editor.commands.setContent(incoming, false);
        if (!incoming && current && current !== '<p></p>') editor.commands.clearContent();
    }, [value, editor]);

    const promptLink = useCallback(() => {
        if (!editor) return;
        const prev = editor.getAttributes('link').href;
        // eslint-disable-next-line no-alert
        const url = window.prompt('Link URL (leave empty to remove)', prev || 'https://');
        if (url === null) return;
        if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    if (!editor) return null;

    const is = (name, attrs) => editor.isActive(name, attrs);

    return (
        <div data-testid={testid}>
            <div
                style={{
                    display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline',
                    borderBottom: '1px solid var(--rule)', marginBottom: 0, paddingBottom: 6,
                }}
                role="toolbar"
                aria-label="Formatting"
            >
                <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} style={btn(is('paragraph') && !is('heading') && !is('blockquote'))} data-testid={`${testid}-paragraph`}>Paragraph</button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} style={btn(is('heading', { level: 2 }))} data-testid={`${testid}-h2`}>H2</button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} style={btn(is('heading', { level: 3 }))} data-testid={`${testid}-h3`}>H3</button>
                <span style={{ width: 1, height: 14, background: 'var(--rule)', margin: '0 8px' }} aria-hidden />
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} style={{ ...btn(is('bold')), fontWeight: 600 }} data-testid={`${testid}-bold`}>Bold</button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} style={{ ...btn(is('italic')), fontStyle: 'italic' }} data-testid={`${testid}-italic`}>Italic</button>
                <span style={{ width: 1, height: 14, background: 'var(--rule)', margin: '0 8px' }} aria-hidden />
                <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} style={btn(is('blockquote'))} data-testid={`${testid}-quote`}>Quote</button>
                <button type="button" onClick={promptLink} style={btn(is('link'))} data-testid={`${testid}-link`}>Link</button>
            </div>
            <EditorContent editor={editor} style={editorProseStyles} className="rich-editor" data-testid={`${testid}-surface`} />
            <p className="caption" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
                Only paragraph, H2, H3, bold, italic, quote and link are enabled — typography is governed by the Maison Touré design system.
            </p>
        </div>
    );
}
